import { Msg } from "@omnia/shared/react/Msg";
import { compareArrays } from "@omnia/shared/utils/array";
import { Checkbox } from "../../react/checkbox";
import { classes } from "../../react/react";
import { DialogFooterButtons } from "@omnia/shared/react/DialogFooter";

export interface YearPickerOption {
    year: number;
    selected?: boolean;
    required?: boolean;
}

export interface YearsPopupProps {
    /**
     * Options for the picker.
     */
    options: YearPickerOption[];
    /**
     * Event handler called when the selection is changed.
     */
    onChange: (pickerYears: YearPickerOption[]) => void;
    /**
     * If specified (and present in the options), "Current Year" will appear as a shortcut button.
     */
    currentYear?: number;
    /**
     * Callback when the popup should be closed.
     */
    close: () => void;
}

export function YearsPopup({ options = [], onChange, close, currentYear }: YearsPopupProps) {
    const [popupState, setPopupState] = React.useState<YearPickerOption[]>(options);

    const toggleYear = (year: number) => () => {
        setPopupState((prev) => prev.map((y) => (y.year === year ? { ...y, selected: !y.selected } : y)));
    };

    const handleConfirm = () => {
        onChange(popupState);
        close();
    };

    const handleCancel = () => close();

    const selectAll = (select: boolean) => {
        setPopupState((prev) =>
            prev.map((y) => {
                return { ...y, selected: select || y.required };
            })
        );
    };

    const canConfirm = !compareArrays(
        options.filter((x) => x.selected).map((x) => x.year),
        popupState.filter((x) => x.selected).map((x) => x.year)
    );

    const canSelectNone = !popupState.some((x) => x.required);

    const canSelectAll = !popupState.every((x) => x.selected);

    const showCurrentYearButton = currentYear !== undefined && popupState.some((x) => x.year === currentYear);

    const canSelectCurrentYear = !popupState.every((x) => (x.year === currentYear) === x.selected);

    return (
        <div className="omnia-years-picker-popup">
            <ul>
                {popupState.map((year) => {
                    return (
                        <li key={year.year}>
                            <Checkbox
                                checked={year.selected}
                                setChecked={toggleYear(year.year)}
                                disabled={year.required}
                                cssClass={classes(year.required && "required")}
                            >
                                <span>{year.year}</span>
                            </Checkbox>
                        </li>
                    );
                })}
            </ul>

            <div className="actions">
                {canSelectNone ? (
                    <button
                        type="button"
                        className="btn btn-link"
                        onClick={() => selectAll(false)}
                        disabled={!canSelectNone}
                    >
                        None
                    </button>
                ) : null}
                {showCurrentYearButton ? (
                    <button
                        type="button"
                        className="btn btn-link"
                        onClick={() => selectAll(false)}
                        disabled={!canSelectCurrentYear}
                    >
                        Current Year
                    </button>
                ) : null}
                <button type="button" className="btn btn-link" onClick={() => selectAll(true)} disabled={!canSelectAll}>
                    All
                </button>
            </div>

            <DialogFooterButtons
                canCommit={canConfirm}
                hasChanges={true}
                onCommit={handleConfirm}
                onClose={handleCancel}
                saveButtonText={<Msg>Ok</Msg>}
            />
        </div>
    );
}
